import type { JsonValue } from "type-fest";

/**
 * Converts an Error object to a serializable object for analytics
 */
export function serializeError(error: unknown): Record<string, JsonValue> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack ?? null,
    };
  }

  return { message: String(error) };
}

/**
 * Converts a value to a JSON-serializable value
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function toJsonValue(value: unknown): JsonValue {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof value === "function" || typeof value === "symbol") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(toJsonValue);
  }

  if (typeof value === "object") {
    try {
      const result: Record<string, JsonValue> = {};

      for (const key in value) {
        if (Object.hasOwn(value, key)) {
          result[key] = toJsonValue((value as Record<string, unknown>)[key]);
        }
      }

      return result;
    } catch {
      return JSON.stringify(value);
    }
  }

  return JSON.stringify(value);
}

/**
 * Converts an object to a record with JSON-serializable values
 */
export function toJsonRecord(object: Record<string, unknown>): Record<string, JsonValue> {
  const result: Record<string, JsonValue> = {};

  for (const key in object) {
    if (Object.hasOwn(object, key)) {
      result[key] = toJsonValue(object[key]);
    }
  }

  return result;
}
